import {
  ContactPersonInfo,
  RegulatorType,
  NameInfo,
  ContactInfo,
  LocationInfo,
  Contact,
  isOnlyUsedForElectricityBilling,
} from "../data/models/ProjectDetails";

import { Role, ValidationLevel } from "@/data/models/User";
import Vue from "vue";
import { UserCapability } from "@/data/models/UserCapability";
import { UserCapabilityOption } from "@/data/models/UserCapabilityOption";
import { i18n } from "@/plugins/vuetify";
import { formatApartmentDetails, joinApartmentLabel } from "@/utils/ApartmentLabel";

export function formatFloat(
  toFormat: number | undefined | null,
  precision: number
) {
  if (toFormat === undefined || toFormat === null) {
    return "";
  }

  const result = toFormat.toLocaleString(i18n.locale, {
    maximumFractionDigits: precision,
  });

  return result;
}

export function parseLocalizedFloat(
  toParse: string | null | undefined
): number {
  if (!toParse || toParse.trim() === "") {
    return NaN;
  }

  const decimalSeparator = (1.1).toLocaleString(i18n.locale).substring(1, 2);
  // Drop the thousands separator first. French and Polish group with a (narrow)
  // no-break space, and parseFloat stops at it, so "1 234,5" used to read as 1.
  const withoutGrouping = toParse.replace(/\s/g, "");
  const normalized = decimalSeparator === ","
    ? withoutGrouping.replace(/\./g, "").replace(",", ".")
    : withoutGrouping.replace(/,/g, "");

  return parseFloat(normalized);
}

export function formatKWh(vue: Vue, kwh: number, precision?: number) {
  return vue
    .$t("common.formatKWh", [formatFloat(kwh, precision ?? 2)])
    .toString();
}

export function filterStrings(array: Array<string | undefined | null>) {
  return array.filter((value) => value && value !== "");
}

export function localiseRegulatorType(vue: Vue, regulatorType: RegulatorType) {
  switch (regulatorType) {
    case RegulatorType.VU_FIELD_SALES: {
      return vue.$t("regulatorTypes.vuFieldSales").toString();
    }
    case RegulatorType.VU_AGENCY: {
      return vue.$t("regulatorTypes.vuAgency").toString();
    }
    case RegulatorType.VU_INTERNAL_SALES: {
      return vue.$t("regulatorTypes.vuInternalSales").toString();
    }
    case RegulatorType.EXTERNAL_REGULATOR: {
      return vue.$t("regulatorTypes.externalRegulator").toString();
    }
    case RegulatorType.AGENT: {
      return vue.$t("regulatorTypes.agent").toString();
    }
  }
}

export function formatApartmentNumber(vue: Vue, contactPersonNumber: number) {
  return vue.$t("clientData.formatApartment", [contactPersonNumber]).toString();
}

/**
 * Formats the display name of an electricity-billing apartment, e.g. "Strombezug 1".
 *
 * These live in their own numbering namespace and must never be labelled "Wohnung" — the project
 * app shows them as "Strombezug N", and calling the same thing an apartment in the portal makes it
 * look like a real apartment the project does not have.
 *
 * @param {Vue} vue - Component instance used for translation lookup.
 * @param {number} contactPersonNumber - The Strombezug number (already offset-corrected).
 * @returns {string} The localised "Strombezug N" label.
 */
export function formatElectricitySupplyNumber(vue: Vue, contactPersonNumber: number) {
  return vue
    .$t("clientData.formatElectricitySupply", [contactPersonNumber])
    .toString();
}

/**
 * The apartment label: "Wohnung 2: H. Hubschneider (Keller)" in a project with several flats,
 * "Wohnung H. Hubschneider (Keller)" in one with a single flat, see {@link formatApartmentDetails}.
 * A Strombezug keeps its own "Strombezug 1 (Meier)".
 *
 * @param hasMoreThanOneApartment whether the project has several real flats (Strombezug not counted)
 */
export function formatApartmentName(vue: Vue, contactInfo: ContactPersonInfo, hasMoreThanOneApartment = true) {
  if (isOnlyUsedForElectricityBilling(contactInfo)) {
    const supply = formatElectricitySupplyNumber(vue, contactInfo.contactPersonNumber);
    const additionalInfo = filterStrings([contactInfo.lastName, contactInfo.floor]);

    return additionalInfo.length === 0 ? supply : `${supply} (${additionalInfo.join(", ")})`;
  }

  const apartment = hasMoreThanOneApartment
    ? formatApartmentNumber(vue, contactInfo.contactPersonNumber)
    : vue.$t("projectLogs.noteLogs.apartment").toString();

  return joinApartmentLabel(apartment, formatApartmentDetails(contactInfo), hasMoreThanOneApartment);
}

export function formatPhoneNumbers(vue: Vue, contactInfo: ContactInfo) {
  const phone = vue.$t("clientData.phone", [contactInfo.phone]).toString();
  const mobile = vue
    .$t("clientData.mobilePhone", [contactInfo.mobilePhone])
    .toString();

  if (contactInfo.phone?.trim() && contactInfo.mobilePhone?.trim()) {
    return `${phone}, ${mobile}`;
  } else if (contactInfo.phone?.trim()) {
    return phone;
  } else if (contactInfo.mobilePhone?.trim()) {
    return mobile;
  } else {
    return undefined;
  }
}

export function joinToString(
  toJoin: Array<string | undefined | null>,
  separator: string
) {
  return filterStrings(toJoin).join(separator);
}

export function formatContactInfo(vue: Vue, contactInfo: ContactInfo) {
  return filterStrings([
    contactInfo.emailAddress,
    formatPhoneNumbers(vue, contactInfo),
  ]).join("\n");
}

export function formatNameInfo(nameInfo: NameInfo) {
  return filterStrings([
    nameInfo.title,
    nameInfo.firstName,
    nameInfo.lastName,
  ]).join(" ");
}

/**
 * Writes an address on one line, in the order the client configured under Einstellungen >
 * Einstellungen Niederlassung > Adressformat.
 *
 * Every screen that displays an address goes through here (project client data, project list,
 * branch list, cockpit), so one setting governs all of them. A client that has never opened the
 * setting gets the default "Straße Hausnummer, PLZ Ort"; parts the address does not carry are
 * skipped, so no empty commas are printed.
 *
 * @param {LocationInfo} locationInfo - The address to write out.
 * @returns {string} The formatted address, empty when the address carries nothing.
 */
export function formatLocationInfo(locationInfo: LocationInfo) {
  // Lazy-require for the reason given in Utils.ts: this file is imported by model files, and a
  // top-level import of the store module - or of AddressTemplate, which pulls in Settings.ts and
  // through it ProjectDetails.ts, which imports this file - closes a circular chain at load time.
  /* eslint-disable @typescript-eslint/no-var-requires */
  const { renderAddressTemplate } =
    require("@/utils/AddressTemplate") as typeof import("@/utils/AddressTemplate");
  const { CompanySettingsModule } =
    require("@/store/modules/CompanySettingsModule") as typeof import("@/store/modules/CompanySettingsModule");
  /* eslint-enable @typescript-eslint/no-var-requires */

  return renderAddressTemplate(
    CompanySettingsModule.companySettings?.addressFormatTemplate,
    {
      street: locationInfo.street,
      houseNumber: locationInfo.houseNumber,
      zipCode: locationInfo.zipCode,
      city: locationInfo.city,
      country: locationInfo.countryCode,
    }
  );
}

export function formatUserValidationLevel(
  vue: Vue,
  validationLevel?: ValidationLevel
) {
  switch (validationLevel) {
    case ValidationLevel.LOW: {
      return vue.$t("users.validationLevel.low").toString();
    }
    case ValidationLevel.NORMAL: {
      return vue.$t("users.validationLevel.normal").toString();
    }
    case ValidationLevel.STRICT: {
      return vue.$t("users.validationLevel.strict").toString();
    }
    case ValidationLevel.NOT_SPECIFIED: {
      return "";
    }
  }

  return "";
}

export function formatUserRole(vue: Vue, role?: Role) {
  switch (role) {
    case Role.ADMIN: {
      return vue.$t("users.role.sysAdmin").toString();
    }
    case Role.PROJECT_MANAGER: {
      return vue.$t("users.role.projectManager").toString();
    }
    case Role.TECHNICIAN_1: {
      return vue.$t("users.role.technician1").toString();
    }
    case Role.TECHNICIAN_2: {
      return vue.$t("users.role.technician2").toString();
    }
    case Role.LEAK_DETECTION: {
      return vue.$t("users.role.leakDetection").toString();
    }
    case Role.OFFICE: {
      return vue.$t("users.role.office").toString();
    }
    case Role.TECHNICIAN_AND_LEAK_DETECTION: {
      return vue.$t("users.role.technicanAndLeakDetection").toString();
    }
    case Role.CUSTOM_ROLE_1: {
      return vue.$t("users.role.customRole1").toString();
    }
    case Role.CUSTOM_ROLE_2: {
      return vue.$t("users.role.customRole2").toString();
    }
    case Role.CUSTOM_ROLE_3: {
      return vue.$t("users.role.customRole3").toString();
    }
    case Role.CUSTOM_ROLE_4: {
      return vue.$t("users.role.customRole4").toString();
    }
    case Role.CUSTOM_ROLE_5: {
      return vue.$t("users.role.customRole5").toString();
    }
  }
  return "";
}

export function formatUserRoleDescription(vue: Vue, role?: Role) {
  switch (role) {
    case Role.ADMIN: {
      return vue.$t("users.role.na").toString();
    }
    case Role.PROJECT_MANAGER: {
      return vue.$t("users.role.na").toString();
    }
    case Role.TECHNICIAN_1: {
      return vue.$t("users.role.na").toString();
    }
    case Role.TECHNICIAN_2: {
      return vue.$t("users.role.na").toString();
    }
    case Role.LEAK_DETECTION: {
      return vue.$t("users.role.na").toString();
    }
    case Role.OFFICE: {
      return vue.$t("users.role.na").toString();
    }
    case Role.TECHNICIAN_AND_LEAK_DETECTION: {
      return vue.$t("users.role.na").toString();
    }
    case Role.CUSTOM_ROLE_1: {
      return vue.$t("users.role.na").toString();
    }
    case Role.CUSTOM_ROLE_2: {
      return vue.$t("users.role.na").toString();
    }
    case Role.CUSTOM_ROLE_3: {
      return vue.$t("users.role.na").toString();
    }
    case Role.CUSTOM_ROLE_4: {
      return vue.$t("users.role.na").toString();
    }
    case Role.CUSTOM_ROLE_5: {
      return vue.$t("users.role.na").toString();
    }
  }
  return "";
}

export function localiceCapability(capability: UserCapability, vue: Vue) {
  switch (capability) {
    case UserCapability.EDIT_ADMIN:
      return vue.$t("settings.permissions.editAdmin").toString()
    case UserCapability.EDIT_USER:
      return vue.$t("settings.permissions.editUser").toString()
    case UserCapability.EDIT_VALIDATION:
      return vue.$t("settings.permissions.editValidation").toString()
    case UserCapability.SEE_ALL_SETTINGS:
      return vue.$t("settings.permissions.seeAllSettings").toString()
    case UserCapability.BASIC_LICENCE:
      return vue.$t("settings.permissions.basicLicence").toString()
    case UserCapability.SEE_ALL_PROJECTS:
      return vue.$t("settings.permissions.seeAllProjects").toString()
    case UserCapability.CREATE_PROJECT:
      return vue.$t("settings.permissions.createProject").toString()
    case UserCapability.DELETE_PROJECT:
      return vue.$t("settings.permissions.deleteProject").toString()
    case UserCapability.REOPEN_COMPLETED_PHASES:
      return vue.$t("settings.permissions.reopenCompletedPhases").toString()
    case UserCapability.EDIT_PROJECT:
      return vue.$t("settings.permissions.editProjects").toString()
    case UserCapability.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION:
      return vue.$t("settings.permissions.recording").toString()
    case UserCapability.DRYING_AND_REPAIR:
      return vue.$t("settings.permissions.dryingAndRecording").toString()
    case UserCapability.OFFICE:
      return vue.$t("settings.permissions.office").toString()
    case UserCapability.PROJECT_MANAGEMENT:
      return vue.$t("settings.permissions.projectManagement").toString()
    case UserCapability.ASSIGN_PROJECT_TO_SELF:
      return vue.$t("settings.permissions.selfAssignProjects").toString()
    case UserCapability.ASSIGN_PROJECT_TO_OTHERS:
      return vue.$t("settings.permissions.assignProjectToOthers").toString()
    case UserCapability.PROJECT_SUPERVISION:
      return vue.$t("settings.permissions.projectsSuperVision").toString()
    case UserCapability.EDIT_DEVICE_SETTINGS:
      return vue.$t("settings.permissions.defineDeviceCategories").toString() //
    case UserCapability.IMPORT_DEVICES:
      return vue.$t("settings.permissions.importDevicesCategories").toString()
    case UserCapability.EDIT_DEVICES:
      return vue.$t("settings.permissions.editDevices").toString()
    case UserCapability.LEAK_DETECTION:
      return vue.$t("settings.permissions.leakDetection").toString()
    case UserCapability.DAMAGE_ASSESSMENT_LARGE:
      return vue.$t("settings.permissions.damageAssessmentLarge").toString()
    case UserCapability.DRYING:
      return vue.$t("settings.permissions.drying").toString()
    case UserCapability.REPAIR:
      return vue.$t("settings.permissions.repair").toString()
    case UserCapability.HAS_VEHICLE:
      return vue.$t("settings.permissions.haveVehicle").toString()
    case UserCapability.CLIENT_ADMINISTRATION:
      return vue.$t("settings.permissions.clientAdministration").toString()
    case UserCapability.DEVICE_ADMINISTRATION:
      return vue.$t("settings.permissions.deviceAdministration").toString()
    case UserCapability.SETTINGS_ADMINISTRATION:
      return vue.$t("settings.permissions.settingsAdministration").toString()
    case UserCapability.RATE_DRYINGS:
      return vue.$t("settings.permissions.rateDryings").toString()
    case UserCapability.MANAGE_PRODUCTION:
      return vue.$t("settings.permissions.manageProduction").toString()
    case UserCapability.MANAGE_CLIENTS:
      return vue.$t("settings.permissions.manageClients").toString()

    // return vue.$t("common.todo") + " : " + capability.toString()
  }
}

export function formatUserRoleCapability(vue: Vue, capabilityOption?: UserCapabilityOption) {
  switch (capabilityOption) {
    case UserCapabilityOption.YES: {
      return vue.$t("settings.roleManagement.capabilityOption.yes").toString();
    }
    case UserCapabilityOption.NO: {
      return vue.$t("settings.roleManagement.capabilityOption.no").toString();
    }
    case UserCapabilityOption.CONDITIONAL: {
      return vue.$t("settings.roleManagement.capabilityOption.conditional").toString();
    }
  }
}

export function formatTemperatuers(
  vue: Vue,
  isEdgePanel: boolean,
  wallTemp?: number,
  groundTemp?: number
) {
  const stringWallTemp =
    wallTemp !== undefined
      ? wallTemp.toLocaleString(undefined, { maximumFractionDigits: 1 })
      : "-";

  if (isEdgePanel) {
    const stringGroundTemp =
      groundTemp !== undefined
        ? groundTemp.toLocaleString(undefined, { maximumFractionDigits: 1 })
        : "-";

    return vue
      .$t("panelDetail.formatTemperatureWallAndGround", [
        stringWallTemp,
        stringGroundTemp,
      ])
      .toString();
  } else {
    return vue
      .$t("panelDetail.formatTemperatureWall", [stringWallTemp])
      .toString();
  }
}

export function formatCompleteContactInfo(vue: Vue, contact: Contact) {
  const name = joinToString(
    [
      formatNameInfo(contact),
      contact.floor && contact.floor !== "" ? contact.floor : undefined,
    ],
    ", "
  );

  return joinToString(
    [
      name,
      formatContactInfo(vue, contact),
      contact.note
        ? vue.$t("clientData.formatNote", [contact.note]).toString()
        : undefined,
    ],
    "\n"
  );
}

export function formatCompleteContactPersonInfo(
  vue: Vue,
  contactInfo: ContactPersonInfo
) {
  const name = joinToString(
    [
      formatNameInfo(contactInfo),
      contactInfo.floor && contactInfo.floor !== ""
        ? contactInfo.floor
        : undefined,
    ],
    ", "
  );

  return joinToString(
    [
      name,
      formatContactInfo(vue, contactInfo),
      contactInfo.note
        ? vue.$t("clientData.formatNote", [contactInfo.note]).toString()
        : undefined,
    ],
    "\n"
  );
}

export function formatContactPerson(
  vue: Vue,
  contactPersonNumber: number,
  contactPerson: ContactPersonInfo | null | undefined
) {
  if (!contactPersonNumber) {
    return vue.$t("projectStatus.allApartments").toString();
  } else {
    if (contactPerson) {
      return formatApartmentNumber(vue, contactPerson.contactPersonNumber);
    } else {
      return vue
        .$t("clientData.formatApartment", [contactPersonNumber])
        .toString();
    }
  }
}
